'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { VariantProps } from 'tailwind-variants';

import { UiKitSelectBase } from '@/fo/ui/kit/select/base';
import {
  MappedProduct,
  UniversalVariantsResult,
  SelectedAttributes,
  filterAvailableOptions,
  findProductBySelectedAttributes,
  ConfigurableVariants
} from '@/fo/util/map-product-data';

import { styles } from './UiProductOptionsBase.styles';

type Variants = VariantProps<typeof styles>;

interface SelectOption {
  label: string;
  value: string;
  stockStatus?: string | null;
  product?: MappedProduct;
}

export interface UiProductOptionsUniversalProps {
  className?: string;
  selectedProduct: MappedProduct;
  options?: UniversalVariantsResult;
  configurableVariants?: ConfigurableVariants;
  size: Variants['size'];
  onOptionChange?: (product: MappedProduct | null) => void;
}

export function UiProductOptionsUniversal({
  selectedProduct,
  className,
  options,
  configurableVariants,
  onOptionChange
}: UiProductOptionsUniversalProps) {
  const t = useTranslations();
  const [selectedAttributes, setSelectedAttributes] =
    useState<SelectedAttributes>({});

  useEffect(() => {
    setSelectedAttributes(selectedProduct.selectedAttributes || {});
  }, [selectedProduct, selectedProduct.selectedAttributes]);

  // Мапа переводов атрибутов
  const attributeMap: Record<string, string> = {
    volume: t('Volume'),
    size: t('Size'),
    color: t('Color'),
    material: t('Material'),
    weight: t('Weight')
  };

  if (!options || options.length === 0 || !configurableVariants) {
    return null;
  }

  // Фильтруем доступные опции на основе выбранных атрибутов
  const filteredOptions = filterAvailableOptions(
    options,
    selectedAttributes,
    configurableVariants
  );

  const handleAttributeChange = (
    attributeCode: string,
    option: SelectOption | null
  ) => {
    const newSelectedAttributes = { ...selectedAttributes };

    if (!option) {
      // Удаляем выбранное значение для этого атрибута
      // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
      delete newSelectedAttributes[attributeCode];

      // Удаляем все последующие атрибуты (сбрасываем выбор после измененного)
      const attributeIndex = options.findIndex(
        (group) => group.attributeCode === attributeCode
      );
      const subsequentAttributes = options
        .slice(attributeIndex + 1)
        .map((group) => group.attributeCode);
      subsequentAttributes.forEach((code) => {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newSelectedAttributes[code];
      });
    } else {
      // Устанавливаем новое значение
      newSelectedAttributes[attributeCode] = parseInt(option.value);

      // Удаляем все последующие атрибуты (сбрасываем выбор после измененного)
      const attributeIndex = options.findIndex(
        (group) => group.attributeCode === attributeCode
      );
      const subsequentAttributes = options
        .slice(attributeIndex + 1)
        .map((group) => group.attributeCode);
      subsequentAttributes.forEach((code) => {
        // eslint-disable-next-line @typescript-eslint/no-dynamic-delete
        delete newSelectedAttributes[code];
      });
    }

    setSelectedAttributes(newSelectedAttributes);

    // Ищем продукт по выбранным атрибутам
    const product = findProductBySelectedAttributes(
      newSelectedAttributes,
      configurableVariants,
      t
    );

    onOptionChange?.(product ?? null);
  };

  return (
    <div className={className}>
      {filteredOptions.map((group, index) => {
        const selectOptions: SelectOption[] = group.options.map((option) => ({
          label: option.label,
          value: option.valueIndex.toString(),
          stockStatus: option.stockStatus,
          product: option.product
        }));

        const selectedOption = selectOptions.find(
          (option) =>
            option.value === selectedAttributes[group.attributeCode]?.toString()
        );

        // Определяем, должен ли селект быть отключен
        const isDisabled =
          selectOptions.length === 0 ||
          (index > 0 && !Object.keys(selectedAttributes).length);

        return (
          <div
            key={group.attributeCode}
            className="mb-4"
          >
            <UiKitSelectBase
              label={group.attributeLabel}
              options={selectOptions}
              selected={selectedOption || null}
              onChange={(option) =>
                handleAttributeChange(group.attributeCode, option)
              }
              placeholder={
                attributeMap[group.attributeCode] || group?.attributeLabel || ''
              }
              disabled={isDisabled}
            />
          </div>
        );
      })}

      {/* Показываем выбранные варианты */}
      {Object.keys(selectedAttributes).length > 0 && (
        <div className="mt-2 text-sm text-gray-600">
          {Object.entries(selectedAttributes).map(
            ([code, valueIndex], index) => {
              const group = options.find((g) => g.attributeCode === code);
              const option = group?.options.find(
                (o) => o.valueIndex === valueIndex
              );
              const attributeLabel =
                attributeMap[code] || group?.attributeLabel || '';
              return (
                <span key={code}>
                  {attributeLabel}: {option?.label}
                  {index < Object.keys(selectedAttributes).length - 1 && ', '}
                </span>
              );
            }
          )}
        </div>
      )}
    </div>
  );
}
